(() => {
  const SELECTORS = {
    root: '[data-sms-faq-root]',
    seed: '[data-faq-seed]',
    tab: '[data-faq-tab]',
    list: '[data-faq-list]',
    search: '[data-faq-search]',
    status: '[data-faq-status]',
    empty: '[data-faq-empty]',
    loadMore: '[data-faq-load-more]',
    activeLabel: '[data-active-category-label]',
    activeCount: '[data-active-category-count]'
  };

  const CATEGORY_LABELS = {
    'orders-and-shipping': 'Orders & Shipping',
    returns: 'Returns',
    'product-questions': 'Product Questions',
    installation: 'Installation',
    warranty: 'Warranty',
    financing: 'Financing',
    'account-and-support': 'Account & Support'
  };

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function slugify(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function normalizeType(value) {
    const slug = slugify(value);

    const aliases = {
      'orders-shipping': 'orders-and-shipping',
      'orders-and-delivery': 'orders-and-shipping',
      'shipping-delivery': 'orders-and-shipping',
      'shipping-and-delivery': 'orders-and-shipping',
      'multi-brand-orders': 'orders-and-shipping',
      'account-support': 'account-and-support',
      'general-account': 'account-and-support',
      'product-question': 'product-questions',
      products: 'product-questions',
      'mini-splits': 'product-questions',
      minisplits: 'product-questions'
    };

    return aliases[slug] || slug;
  }

  function labelFromType(typeHandle, rawType) {
    return CATEGORY_LABELS[typeHandle] || rawType || typeHandle || 'FAQ';
  }

  function stripHtml(value) {
    const div = document.createElement('div');
    div.innerHTML = String(value || '');
    return div.textContent || div.innerText || '';
  }

  function plainTextToHtml(value) {
    return escapeHtml(value).replace(/\n/g, '<br>');
  }

  function richTextNodeToHtml(node) {
    if (!node || typeof node !== 'object') return '';

    if (node.type === 'root') {
      return (node.children || []).map(richTextNodeToHtml).join('');
    }

    if (node.type === 'paragraph') {
      const inner = (node.children || []).map(richTextNodeToHtml).join('');
      return inner ? `<p>${inner}</p>` : '';
    }

    if (node.type === 'heading') {
      const level = Math.min(Math.max(Number(node.level || 3), 2), 4);
      const inner = (node.children || []).map(richTextNodeToHtml).join('');
      return `<h${level}>${inner}</h${level}>`;
    }

    if (node.type === 'list') {
      const tag = node.listType === 'ordered' ? 'ol' : 'ul';
      const inner = (node.children || []).map(richTextNodeToHtml).join('');
      return `<${tag}>${inner}</${tag}>`;
    }

    if (node.type === 'list-item') {
      const inner = (node.children || []).map(richTextNodeToHtml).join('');
      return `<li>${inner}</li>`;
    }

    if (node.type === 'link') {
      const href = escapeHtml(node.url || '#');
      const inner = (node.children || []).map(richTextNodeToHtml).join('');
      return `<a href="${href}">${inner}</a>`;
    }

    if (node.type === 'text') {
      let text = escapeHtml(node.value || '');

      if (node.bold) text = `<strong>${text}</strong>`;
      if (node.italic) text = `<em>${text}</em>`;

      return text;
    }

    return (node.children || []).map(richTextNodeToHtml).join('');
  }

  function richTextValueToHtml(value) {
    if (!value) return '';

    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      return richTextNodeToHtml(parsed);
    } catch (error) {
      return plainTextToHtml(value);
    }
  }

  function readSeedData(root) {
    const seed = root.querySelector(SELECTORS.seed);
    if (!seed) return [];

    try {
      const parsed = JSON.parse(seed.textContent || '[]');

      return parsed
        .filter((item) => item && item.question)
        .map((item) => {
          const typeHandle = normalizeType(item.typeHandle || item.type);
          const typeLabel = labelFromType(typeHandle, item.type);

          return {
            handle: item.handle || slugify(item.question),
            type: item.type || '',
            typeHandle,
            typeLabel,
            question: item.question || '',
            answerHtml: item.answerHtml || plainTextToHtml(item.answer || ''),
            answerText: item.answerText || stripHtml(item.answerHtml || item.answer || '')
          };
        });
    } catch (error) {
      console.warn('[SMS FAQ] Could not parse seeded FAQ data.', error);
      return [];
    }
  }

  function metaobjectNodeToFaq(node) {
    const fieldMap = {};

    (node.fields || []).forEach((field) => {
      fieldMap[field.key] = field;
    });

    const typeField = fieldMap.type || {};
    const questionField = fieldMap.question || {};
    const answerField = fieldMap.answer || {};

    const type = typeField.value || '';
    const typeHandle = normalizeType(type);
    const typeLabel = labelFromType(typeHandle, type);
    const question = questionField.value || '';

    let answerHtml = '';
    let answerText = '';

    if ((answerField.type || '').includes('rich_text')) {
      answerHtml = richTextValueToHtml(answerField.value || '');
      answerText = stripHtml(answerHtml);
    } else {
      answerHtml = plainTextToHtml(answerField.value || '');
      answerText = answerField.value || '';
    }

    return {
      handle: node.handle || slugify(question),
      type,
      typeHandle,
      typeLabel,
      question,
      answerHtml,
      answerText
    };
  }

  async function fetchAllFaqs(root) {
    const token = (root.dataset.storefrontToken || '').trim();
    const apiVersion = root.dataset.apiVersion || '2026-04';
    const requestSize = Math.min(Math.max(parseInt(root.dataset.requestSize || '250', 10), 1), 250);

    if (!token) return null;

    const endpoint = `${window.location.origin}/api/${apiVersion}/graphql.json`;

    const query = `
      query SmsFaqMetaobjects($first: Int!, $after: String) {
        metaobjects(type: "faq_questions", first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            handle
            fields {
              key
              value
              type
            }
          }
        }
      }
    `;

    let after = null;
    let hasNextPage = true;
    const allFaqs = [];
    let safetyCounter = 0;

    while (hasNextPage) {
      safetyCounter += 1;

      if (safetyCounter > 100) {
        throw new Error('Pagination safety stop reached.');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({
          query,
          variables: {
            first: requestSize,
            after
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Storefront API request failed: ${response.status}`);
      }

      const payload = await response.json();

      if (payload.errors && payload.errors.length) {
        throw new Error(payload.errors.map((error) => error.message).join(', '));
      }

      const connection = payload?.data?.metaobjects;

      if (!connection) {
        throw new Error('No metaobjects connection returned.');
      }

      (connection.nodes || []).forEach((node) => {
        const faq = metaobjectNodeToFaq(node);
        if (faq.question) allFaqs.push(faq);
      });

      hasNextPage = Boolean(connection.pageInfo?.hasNextPage);
      after = connection.pageInfo?.endCursor || null;
    }

    return allFaqs;
  }

  function uniqueFaqs(faqs) {
    const seen = new Set();

    return faqs.filter((faq) => {
      const key = faq.handle || `${faq.typeHandle}-${slugify(faq.question)}`;

      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  }

  function initSmsFaq(root) {
    if (!root || root.dataset.smsFaqInitialized === 'true') return;
    root.dataset.smsFaqInitialized = 'true';

    const tabs = Array.from(root.querySelectorAll(SELECTORS.tab));
    const list = root.querySelector(SELECTORS.list);
    const search = root.querySelector(SELECTORS.search);
    const status = root.querySelector(SELECTORS.status);
    const empty = root.querySelector(SELECTORS.empty);
    const loadMore = root.querySelector(SELECTORS.loadMore);
    const activeLabel = root.querySelector(SELECTORS.activeLabel);
    const activeCount = root.querySelector(SELECTORS.activeCount);

    const closeOthers = root.dataset.closeOthers === 'true';
    const hideEmptyCategories = root.dataset.hideEmptyCategories === 'true';
    const showCategoryCounts = root.dataset.showCategoryCounts === 'true';
    const renderAllQuestions = root.dataset.renderAllQuestions !== 'false';
    const batchSize = Math.max(parseInt(root.dataset.renderBatchSize || '250', 10), 1);
    const previewCount = Math.max(parseInt(root.dataset.previewCount || '6', 10), 1);
    const openItemsCount = Math.max(parseInt(root.dataset.openItemsCount || '0', 10), 0);

    let allFaqs = readSeedData(root);
    let activeCategory = root.dataset.defaultCategory || tabs[0]?.dataset.faqTab || 'product-questions';
    let filteredFaqs = [];
    let renderedCount = 0;
    let expandedCategory = false;

    function getSearchQuery() {
      return search ? search.value.trim().toLowerCase() : '';
    }

    function isSearchMode() {
      return getSearchQuery().length > 0;
    }

    function setStatus(message, isWarning = false) {
      if (!status) return;

      status.textContent = message || '';
      status.classList.toggle('is-visible', Boolean(message));
      status.classList.toggle('is-warning', Boolean(isWarning));
    }

    function getCategoryLabel(handle) {
      const tab = tabs.find((item) => item.dataset.faqTab === handle);
      return tab?.dataset.faqLabel || labelFromType(handle, handle);
    }

    function updateCounts() {
      const counts = {};

      tabs.forEach((tab) => {
        counts[tab.dataset.faqTab] = 0;
      });

      allFaqs.forEach((faq) => {
        if (counts[faq.typeHandle] === undefined) counts[faq.typeHandle] = 0;
        counts[faq.typeHandle] += 1;
      });

      tabs.forEach((tab) => {
        const handle = tab.dataset.faqTab;
        const count = counts[handle] || 0;
        const countEl = tab.querySelector('[data-faq-tab-count]');

        if (showCategoryCounts && countEl) countEl.textContent = String(count);
        if (hideEmptyCategories) tab.hidden = count === 0;
      });

      if (hideEmptyCategories) {
        const activeTab = tabs.find((tab) => tab.dataset.faqTab === activeCategory && !tab.hidden);
        const firstVisibleTab = tabs.find((tab) => !tab.hidden);

        if (!activeTab && firstVisibleTab) {
          activeCategory = firstVisibleTab.dataset.faqTab;
        }
      }
    }

    function setActiveTab() {
      tabs.forEach((tab) => {
        const isActive = tab.dataset.faqTab === activeCategory;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      if (activeLabel) {
        activeLabel.textContent = isSearchMode()
          ? 'Search Results'
          : getCategoryLabel(activeCategory);
      }
    }

    function getFilteredFaqs() {
      const query = getSearchQuery();

      return allFaqs.filter((faq) => {
        if (query) {
          return (
            String(faq.question || '').toLowerCase().includes(query) ||
            String(faq.answerText || '').toLowerCase().includes(query) ||
            String(faq.typeLabel || '').toLowerCase().includes(query) ||
            String(faq.typeHandle || '').toLowerCase().includes(query)
          );
        }

        return faq.typeHandle === activeCategory;
      });
    }

    function getRenderLimit() {
      if (isSearchMode()) return filteredFaqs.length;
      if (expandedCategory) return filteredFaqs.length;
      return Math.min(previewCount, filteredFaqs.length);
    }

    function createFaqElement(faq, index) {
      const details = document.createElement('details');
      const showTypeBadge = isSearchMode();

      details.className = 'sms-faq__item';
      details.dataset.faqItem = '';
      details.dataset.faqHandle = faq.handle || '';
      details.dataset.faqType = faq.typeHandle || '';

      if (!isSearchMode() && index < openItemsCount) {
        details.open = true;
      }

      details.innerHTML = `
        <summary class="sms-faq__question">
          <span class="sms-faq__question-main">
            <span class="sms-faq__type-badge" ${showTypeBadge ? '' : 'hidden'}>${escapeHtml(faq.typeLabel || 'FAQ')}</span>
            <span class="sms-faq__question-text">${escapeHtml(faq.question)}</span>
          </span>
          <span class="sms-faq__toggle" aria-hidden="true"></span>
        </summary>
        <div class="sms-faq__answer">
          ${faq.answerHtml || ''}
        </div>
      `;

      return details;
    }

    function appendBatch() {
      if (!list) return;

      const start = renderedCount;
      const renderLimit = getRenderLimit();

      const end = renderAllQuestions
        ? renderLimit
        : Math.min(renderedCount + batchSize, renderLimit);

      const fragment = document.createDocumentFragment();

      filteredFaqs.slice(start, end).forEach((faq, batchIndex) => {
        fragment.appendChild(createFaqElement(faq, start + batchIndex));
      });

      list.appendChild(fragment);
      renderedCount = end;

      if (loadMore) {
        const hasHiddenByPreview = !isSearchMode() && !expandedCategory && filteredFaqs.length > previewCount;
        const hasHiddenByBatch = !renderAllQuestions && renderedCount < filteredFaqs.length;
        const shouldShow = hasHiddenByPreview || hasHiddenByBatch;

        loadMore.hidden = !shouldShow;

        if (hasHiddenByPreview) {
          loadMore.textContent = `View all ${filteredFaqs.length} questions`;
        } else if (hasHiddenByBatch) {
          loadMore.textContent = `View more questions (${filteredFaqs.length - renderedCount} left)`;
        }
      }
    }

    function render(reset = true) {
      updateCounts();
      setActiveTab();

      filteredFaqs = getFilteredFaqs();

      if (activeCount) {
        activeCount.textContent = String(filteredFaqs.length);
      }

      if (reset && list) {
        list.innerHTML = '';
        renderedCount = 0;
      }

      if (empty) {
        empty.classList.toggle('is-visible', filteredFaqs.length === 0);
      }

      appendBatch();
    }

    function activateCategory(handle, focusTab = false) {
      activeCategory = handle;
      expandedCategory = false;

      tabs.forEach((tab) => {
        if (focusTab && tab.dataset.faqTab === handle) tab.focus();
      });

      render(true);
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        activateCategory(tab.dataset.faqTab);
      });

      tab.addEventListener('keydown', (event) => {
        const visibleTabs = tabs.filter((item) => !item.hidden);
        const visibleIndex = visibleTabs.indexOf(tab);
        let nextIndex = null;

        if (event.key === 'ArrowRight') nextIndex = visibleIndex + 1;
        if (event.key === 'ArrowLeft') nextIndex = visibleIndex - 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = visibleTabs.length - 1;

        if (nextIndex === null) return;

        event.preventDefault();

        if (nextIndex < 0) nextIndex = visibleTabs.length - 1;
        if (nextIndex >= visibleTabs.length) nextIndex = 0;

        const nextTab = visibleTabs[nextIndex];
        if (nextTab) activateCategory(nextTab.dataset.faqTab, true);
      });
    });

    if (search) {
      search.addEventListener('input', () => {
        expandedCategory = true;
        render(true);
      });
    }

    if (loadMore) {
      loadMore.addEventListener('click', () => {
        expandedCategory = true;
        render(true);
      });
    }

    if (closeOthers && list) {
      list.addEventListener(
        'toggle',
        (event) => {
          const item = event.target;
          if (!item.matches('[data-faq-item]') || !item.open) return;

          list.querySelectorAll('[data-faq-item][open]').forEach((other) => {
            if (other !== item) other.removeAttribute('open');
          });
        },
        true
      );
    }

    render(true);

    const token = (root.dataset.storefrontToken || '').trim();

    if (token) {
      setStatus('');

      fetchAllFaqs(root)
        .then((apiFaqs) => {
          if (Array.isArray(apiFaqs) && apiFaqs.length) {
            allFaqs = uniqueFaqs(apiFaqs);
            setStatus('');
          } else if (allFaqs.length) {
            setStatus('Some FAQ entries may be missing because the Storefront API returned no additional questions.', true);
          } else {
            setStatus('No FAQ questions were found.', true);
          }

          render(true);
        })
        .catch((error) => {
          console.warn('[SMS FAQ] Storefront API load failed.', error);

          if (allFaqs.length) {
            setStatus('Showing fallback FAQ entries only. Check the Storefront API token to load all entries.', true);
          } else {
            setStatus('FAQ questions could not be loaded. Check Storefront API access and token settings.', true);
          }

          render(true);
        });
    } else {
      if (allFaqs.length) {
        setStatus('Showing fallback FAQ entries only. Add a Storefront API token to load all entries.', true);
      } else {
        setStatus('No FAQ questions were found. Add FAQ Questions metaobject entries or add a Storefront API token.', true);
      }
    }
  }

  function initAll(context = document) {
    context.querySelectorAll(SELECTORS.root).forEach(initSmsFaq);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();